/**
 * Shared server-sent events client (plan 03 D18; 00-index "Streaming").
 *
 * One parser for every streamed answer from the AI Employee: the signed-in
 * Command Briefing and Ask panel (`POST /api/ai/chat/stream`, plan 03) and the
 * public driver chat bar (`/ask/stream`, plan 06). Callers add their own auth
 * headers; this module knows only the wire format.
 *
 * Wire contract (ported from the PA shell's apiStreamPost, h2r
 * frontend/src/lib/api-client.ts):
 * - frames are separated by a blank line; `data:` lines carry JSON;
 * - `{"text": "..."}` is a fragment of the answer, appended in order;
 * - `{"step": {"label", "state": "running" | "done", "detail"?}}` is a
 *   "showing the work" line, sent twice per tool with the same label;
 * - `{"tools": [...]}` lists the tools that ran (same shape as /api/ai/chat's
 *   `tool_calls`);
 * - `{"error": "..."}` ends the stream as a failure;
 * - `data: [DONE]` ends it as a success;
 * - lines starting with `:` are comments, used as keep-alive heartbeats.
 *
 * The parsing functions are pure (no fetch, no DOM) so they can be unit tested
 * with `npm run test:unit`. This file must stay free of path aliases and of any
 * domain vocabulary, so it can be lifted into the template (plan 03 CHG-19).
 */

/** One parsed SSE frame, before its `data` is interpreted. */
export type SseFrame =
  | { type: 'event'; event: string; data: string; id?: string }
  | { type: 'comment'; text: string }

/**
 * Parses one raw frame (the text between two blank lines) per the SSE spec:
 * `field: value` lines, one optional space after the colon, multiple `data`
 * lines joined with a newline, and comment lines starting with a colon.
 * Returns null for a frame with neither data nor a comment.
 */
export function parseSseFrame(raw: string): SseFrame | null {
  const dataLines: string[] = []
  const comments: string[] = []
  let event = 'message'
  let id: string | undefined

  for (const line of raw.split(/\r\n|\r|\n/)) {
    if (line === '') continue
    if (line.startsWith(':')) {
      comments.push(line.slice(1).trimStart())
      continue
    }
    const colon = line.indexOf(':')
    const field = colon === -1 ? line : line.slice(0, colon)
    let value = colon === -1 ? '' : line.slice(colon + 1)
    if (value.startsWith(' ')) value = value.slice(1)
    if (field === 'data') dataLines.push(value)
    else if (field === 'event') event = value || 'message'
    else if (field === 'id') id = value
    // `retry` and unknown fields are ignored, as the spec says.
  }

  if (dataLines.length > 0) {
    return id === undefined
      ? { type: 'event', event, data: dataLines.join('\n') }
      : { type: 'event', event, data: dataLines.join('\n'), id }
  }
  if (comments.length > 0) return { type: 'comment', text: comments.join('\n') }
  return null
}

/**
 * Incremental splitter: feed it decoded text chunks in arrival order and it
 * returns the complete frames so far, keeping a partial frame buffered until
 * the rest of it arrives. Handles `\n\n`, `\r\n\r\n` and `\r\r` separators.
 */
export function createSseParser() {
  let buffer = ''
  const separator = /\r\n\r\n|\n\n|\r\r/

  return {
    push(chunk: string): SseFrame[] {
      buffer += chunk
      const frames: SseFrame[] = []
      let match = separator.exec(buffer)
      while (match) {
        const frame = parseSseFrame(buffer.slice(0, match.index))
        buffer = buffer.slice(match.index + match[0].length)
        if (frame) frames.push(frame)
        match = separator.exec(buffer)
      }
      return frames
    },
    /** Parses whatever is left when the stream closes without a final blank line. */
    flush(): SseFrame[] {
      const frame = buffer.trim() ? parseSseFrame(buffer) : null
      buffer = ''
      return frame ? [frame] : []
    },
  }
}

export interface StreamStep {
  label: string
  detail?: string
  state: 'running' | 'done'
}

export interface StreamToolCall {
  id: string
  name: string
  args: Record<string, unknown>
  result?: unknown
}

/** What one `data:` payload means under the wire contract above. */
export type StreamMessage =
  | { kind: 'text'; text: string }
  | { kind: 'step'; step: StreamStep }
  | { kind: 'tools'; tools: StreamToolCall[] }
  | { kind: 'error'; error: string }
  | { kind: 'done' }

function readStep(raw: unknown): StreamStep | null {
  if (!raw || typeof raw !== 'object') return null
  const step = raw as { label?: unknown; detail?: unknown; state?: unknown }
  if (typeof step.label !== 'string' || !step.label) return null
  if (step.state !== 'running' && step.state !== 'done') return null
  return typeof step.detail === 'string' && step.detail
    ? { label: step.label, detail: step.detail, state: step.state }
    : { label: step.label, state: step.state }
}

/**
 * Interprets one `data:` payload. Returns the messages it carries, in the
 * order a caller should apply them (error first, then step, tools, text).
 *
 * A payload that is not JSON is a broken stream and throws: the caller has a
 * whole-answer endpoint to fall back to. A malformed step is dropped instead,
 * because losing one "showing the work" line is better than losing the answer.
 */
export function interpretStreamData(data: string): StreamMessage[] {
  if (data.trim() === '[DONE]') return [{ kind: 'done' }]
  let parsed: unknown
  try {
    parsed = JSON.parse(data)
  } catch {
    throw new StreamError('malformed_frame')
  }
  if (!parsed || typeof parsed !== 'object') throw new StreamError('malformed_frame')
  const frame = parsed as { text?: unknown; error?: unknown; step?: unknown; tools?: unknown }

  if (frame.error !== undefined && frame.error !== null && frame.error !== false) {
    return [{ kind: 'error', error: typeof frame.error === 'string' ? frame.error : 'stream_failed' }]
  }
  const messages: StreamMessage[] = []
  if (frame.step !== undefined) {
    const step = readStep(frame.step)
    if (step) messages.push({ kind: 'step', step })
  }
  if (Array.isArray(frame.tools)) messages.push({ kind: 'tools', tools: frame.tools as StreamToolCall[] })
  if (typeof frame.text === 'string' && frame.text) messages.push({ kind: 'text', text: frame.text })
  return messages
}

/**
 * A stream that failed. `receivedText` tells the caller which recovery applies:
 * before any text, fall back silently to the whole-answer endpoint; after some
 * text, show "lost the thread" with Retry (plan 03 D18).
 */
export class StreamError extends Error {
  readonly status: number | undefined
  receivedText = false

  constructor(message: string, status?: number) {
    super(message)
    this.name = 'StreamError'
    this.status = status
  }
}

/** True when the caller aborted on purpose (panel closed, Escape, new question). */
export function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError'
}

export interface StreamHandlers {
  onText?: (fragment: string) => void
  onStep?: (step: StreamStep) => void
  onTools?: (tools: StreamToolCall[]) => void
  /** Called for each `:` comment frame, so a caller can reset an idle timer. */
  onHeartbeat?: () => void
  /**
   * Called with every JSON object frame, before its text, step or tools are
   * dispatched. For frames that carry fields outside the wire contract above,
   * such as the driver chat bar's final `{"action", "task_key", "source"}`
   * frame (plan 06 CHG-51). Error frames still reject instead.
   */
  onData?: (frame: Record<string, unknown>) => void
}

/**
 * Reads an SSE response body to the end, dispatching each message to the
 * handlers. Resolves with the assembled answer text on `[DONE]` or a clean
 * close; rejects with StreamError on an error frame or a malformed frame.
 * Aborting `signal` cancels the reader and rejects with an AbortError.
 */
export async function readSseStream(
  body: ReadableStream<Uint8Array>,
  handlers: StreamHandlers,
  signal?: AbortSignal
): Promise<string> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  const parser = createSseParser()
  let full = ''

  const onAbort = () => {
    reader.cancel().catch(() => undefined)
  }
  signal?.addEventListener('abort', onAbort, { once: true })

  // Returns true when the stream is finished ([DONE]).
  const dispatch = (frames: SseFrame[]): boolean => {
    for (const frame of frames) {
      if (frame.type === 'comment') {
        handlers.onHeartbeat?.()
        continue
      }
      const messages = interpretStreamData(frame.data)
      if (handlers.onData && !messages.some((m) => m.kind === 'done' || m.kind === 'error')) {
        handlers.onData(JSON.parse(frame.data) as Record<string, unknown>)
      }
      for (const message of messages) {
        if (message.kind === 'done') return true
        if (message.kind === 'error') throw new StreamError(message.error)
        if (message.kind === 'step') handlers.onStep?.(message.step)
        else if (message.kind === 'tools') handlers.onTools?.(message.tools)
        else {
          full += message.text
          handlers.onText?.(message.text)
        }
      }
    }
    return false
  }

  try {
    for (;;) {
      if (signal?.aborted) throw abortError()
      const { done, value } = await reader.read()
      if (signal?.aborted) throw abortError()
      if (done) {
        dispatch(parser.flush())
        return full
      }
      if (dispatch(parser.push(decoder.decode(value, { stream: true })))) {
        reader.cancel().catch(() => undefined)
        return full
      }
    }
  } catch (error) {
    if (error instanceof StreamError) error.receivedText = full.length > 0
    throw error
  } finally {
    signal?.removeEventListener('abort', onAbort)
  }
}

function abortError(): Error {
  const error = new Error('The stream was aborted')
  error.name = 'AbortError'
  return error
}

export interface PostSseOptions {
  /** Extra headers, e.g. the Bearer token added by lib/api-client.ts. */
  headers?: HeadersInit
  signal?: AbortSignal
}

/**
 * POSTs JSON and reads the SSE answer. A non-2xx status (409 when streaming is
 * switched off by Setting, 401, 5xx) or a missing body rejects with a
 * StreamError carrying the status, before any handler runs, so the caller can
 * fall back to the whole-answer endpoint.
 */
export async function postSseStream(
  url: string,
  body: unknown,
  handlers: StreamHandlers,
  options: PostSseOptions = {}
): Promise<string> {
  const headers = new Headers(options.headers)
  headers.set('Content-Type', 'application/json')
  headers.set('Accept', 'text/event-stream')

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal: options.signal,
    cache: 'no-store',
  })
  if (!response.ok || !response.body) {
    throw new StreamError(`stream_http_${response.status}`, response.status)
  }
  return readSseStream(response.body, handlers, options.signal)
}
